import { defineStore } from "pinia";
import {
  AutoaddressProvider,
  DisplayData,
  IAccumulationsSettings,
  IBusinessLine,
  ILocationDataLookup,
  IRiskLookup,
  OrgSettings,
  OSAddressSettings,
  OSApplicationSettings,
  PreciselyProvider,
  RiskOverride,
} from "@/types";
import { useApi, useGammaData, useLocation } from "@/services";
import { useAppConfig, useAxios } from "@/config";
import { AxiosResponse } from "axios";
import { get, isEqual } from "lodash-es";

interface OrgSettingsState {
  settings: null | OrgSettings;
  selectedBusinessLine: IBusinessLine | null;
  searchProviderIndex: number;
  showCoverHolder: boolean;
  selectedRiskOverride: null | RiskOverride;
}

const useLocalOrgSettings = Number(process.env.VUE_APP_USE_LOCAL_ORG_SETTINGS);

export const useOrgSettings = defineStore("orgSettings", {
  state: (): OrgSettingsState => ({
    settings: null,
    searchProviderIndex: 0,
    showCoverHolder: false,
    selectedRiskOverride: null,
    selectedBusinessLine: null,
  }),
  actions: {
    async fetchOrganisationSettings(url = ""): Promise<OrgSettings> {
      if (!url) {
        url = useApi().orgSettings || "";
      }
      return Promise.resolve(
        this.settings ||
          (useLocalOrgSettings
            ? import("@/assets/data/orgSettings.json").then(
                (r) => r.default as unknown as OrgSettings
              )
            : useAxios()
                .$http.get<string, AxiosResponse<OrgSettings>>(url)
                .then((r) => r.data))
      ).then((settings: OrgSettings) => {
        this.updateOrgSettings(settings);
        return settings;
      });
    },

    updateOrgSettings(settings: OrgSettings) {
      const appSettings = settings.applicationSettings;
      if (appSettings?.baseDataApiUrl) {
        useApi().baseDataUrl = appSettings.baseDataApiUrl;
      }

      if (appSettings?.useDatasetRecordGroups) {
        useGammaData().useDatasetRecordGroups = true;
      }

      if (appSettings?.baseGroupsDataApiUrl) {
        useApi().groupsUrl = appSettings.baseGroupsDataApiUrl;
      }

      if (appSettings?.showCoverHolder) {
        useOrgSettings().showCoverHolder = appSettings?.showCoverHolder;
      }

      Object.assign(this, {
        settings,
        searchProviderIndex: 0,
      });
      const sources = get(settings, "mapSettings.sources");
      if (sources) {
        Object.entries(sources)
          .filter((entry: [string, any]) => entry[1]?.type === "gammadata")
          .forEach(([key, source]: [string, any]) => {
            const { columns } = (
              get(settings, "data.dataDisplayLeftPanel") || []
            ).find(
              (e: any) =>
                e &&
                e.businessLine === source?.businessLine &&
                e.dataSetType === source?.dataSetType
            ) || { columns: [] };
            (sources[key] as any).visibleColumns = columns;
          });
      }

      this.setSelectedBusinessLine(settings.businessLines[0]);
      this.setSelectedRiskOverride();
    },

    setSelectedBusinessLine(line: IBusinessLine) {
      if (isEqual(this.selectedBusinessLine, line)) return;
      this.selectedBusinessLine = line;
      useLocation().updateLocation(null);
    },
    setSelectedRiskOverride() {
      if (!this.selectedBusinessLine) return;
      const riskLookup = this.selectedBusinessLine.riskLookups[0];
      if (!riskLookup) return;

      const defualtScenario = riskLookup.scenario;
      this.selectedRiskOverride =
        this.selectedBusinessLine.riskLookupOverrides?.options[0]?.overrides.find(
          (x) =>
            x.riskLookupValue.toLowerCase() === defualtScenario.toLowerCase()
        ) || null;
    },
  },
  getters: {
    googleMapsKey(state): string {
      return (
        state.settings?.applicationSettings?.googleAPIKey ||
        (process.env.VUE_APP_GOOGLE_MAPS_LICENSE as string)
      );
    },
    addressSettings: (state): OSAddressSettings | undefined =>
      state.settings?.addressSettings,
    applicationSettings: (state): OSApplicationSettings | undefined =>
      state.settings?.applicationSettings,
    businessLines: (state): IBusinessLine[] =>
      state.settings?.businessLines || [],
    showRags(): boolean {
      return !!this.settings?.applicationSettings?.showRags;
    },
    currencySymbol(): string {
      return this.settings?.applicationSettings?.currency || "";
    },
    showRiskDetails(): boolean {
      return !!(
        this.settings?.applicationSettings?.showRags &&
        this.settings?.applicationSettings?.showRiskDetails
      );
    },
    showBookUploader(): boolean {
      return !!this.settings?.applicationSettings?.enableBookUploaderRisk;
    },
    showBottomAccumulation(): boolean {
      return !!this.accumulations?.showInBottomPanel;
    },
    showLeftAccumulation(): boolean {
      return !!this.accumulations?.showInLeftPanel;
    },
    riskLookups(): IRiskLookup[] {
      return this.selectedBusinessLine?.riskLookups || [];
    },
    locationDataLookups(): ILocationDataLookup[] {
      return this.selectedBusinessLine?.locationDataLookups || [];
    },
    accumulations: (state): IAccumulationsSettings | null =>
      state.settings?.accumulations || null,
    searchProviders(): (AutoaddressProvider | PreciselyProvider)[] {
      return this.addressSettings?.providers.length
        ? this.addressSettings.providers
        : useAppConfig().baseProviders;
    },
    searchProvider(): AutoaddressProvider | PreciselyProvider {
      return this.searchProviders[this.searchProviderIndex];
    },
    layerSettings(): any[] {
      return this.settings?.mapSettings?.layers || [];
    },
    autoAddressProvider(): AutoaddressProvider | undefined {
      return this.searchProviders.find(
        (x) => x.providerType === "AutoAddress"
      ) as AutoaddressProvider;
    },
    dataDisplayDict(): Map<string, DisplayData> {
      if (!this.settings?.dataDisplay) {
        return new Map<string, DisplayData>();
      }

      return this.settings.dataDisplay.reduce(
        (x, y) => x.set(`${y.businessLine}_${y.dataSetType}`.toLowerCase(), y),
        new Map<string, DisplayData>()
      );
    },
  },
});
